// @ts-nocheck
import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Database, HardDrive, RefreshCw, Trash2,
  Activity, Clock, Download, Upload,
  CheckCircle, Book, Cloud
} from 'lucide-react';
import { apiJSON, apiFetch } from '../config/api';
import notificationService, { handleApiError } from '../utils/notificationService';
import SmartCollections from './SmartCollections';
import { useConfirm } from './ConfirmDialog';
import AuthContext from '../context/AuthContext';
import './DataManagement.css';

/** Code renvoyé par le serveur quand les outils disque historiques sont coupés. */
export const CODE_DISQUE_DESACTIVE = 'LEGACY_DISK_DISABLED';

/**
 * Le serveur a-t-il répondu « sauvegardes disque désactivées » ?
 * Seul le code fait foi : un 503 sans ce code est une vraie panne, à signaler
 * comme telle, pas à maquiller en fonctionnement normal.
 */
async function estDisqueDesactive(reponse) {
  if (!reponse || reponse.status !== 503) return false;
  try {
    const corps = await reponse.json();
    return corps?.error?.code === CODE_DISQUE_DESACTIVE;
  } catch {
    return false;
  }
}

const DataManagement = () => {
  const confirm = useConfirm();
  // Contexte lu sans `useAuth()` : l'écran reste utilisable (et testable) hors
  // AuthProvider, où il retombe sur l'interrogation du serveur.
  const auth = useContext(AuthContext);
  const donneesCloud = auth?.dataLocality === 'cloud';
  // En production « Supabase seul », les routes /api/backup répondent 503
  // LEGACY_DISK_DISABLED : on l'explique au lieu de proposer des boutons qui
  // échouent tous.
  const [disqueDesactive, setDisqueDesactive] = useState(false);
  const sauvegardesLocalesCoupees = donneesCloud || disqueDesactive;
  const [backupStats, setBackupStats] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('backups');

  // Dernière version de `loadData` (elle lit `donneesCloud`) sans relancer
  // l'intervalle à chaque rendu ; le cycle repart quand la configuration du
  // serveur est connue.
  const loadDataRef = useRef(null);
  useEffect(() => {
    const charger = () => loadDataRef.current?.();
    charger();
    const interval = setInterval(charger, 30000);
    return () => clearInterval(interval);
  }, [donneesCloud]);

  /** Statut et liste des sauvegardes disque ; rien n'est appelé si elles sont coupées. */
  const chargerSauvegardes = async () => {
    if (donneesCloud) {
      setBackupStats(null);
      setBackups([]);
      return;
    }

    // `apiFetch` brut : `apiJSON` ne remonte que le statut, et un 503 sans le
    // code LEGACY_DISK_DISABLED doit rester une panne affichée comme telle.
    try {
      const reponse = await apiFetch('backup/status');
      if (await estDisqueDesactive(reponse)) {
        setDisqueDesactive(true);
        setBackupStats(null);
        setBackups([]);
        return;
      }
      setDisqueDesactive(false);
      const backupData = reponse.ok ? await reponse.json() : null;
      if (backupData?.success) {
        setBackupStats(backupData.stats);
      } else {
        throw new Error(`HTTP ${reponse.status}`);
      }
    } catch (error) {
      console.warn('Stats backup non disponibles:', error.message);
      setBackupStats({
        total_backups: 0,
        total_size_mb: 0,
        last_backup: null
      });
    }

    try {
      const backupsData = await apiJSON('backup/list');
      if (backupsData.success) {
        setBackups(backupsData.backups);
      }
    } catch (error) {
      console.warn('Liste backups non disponible:', error.message);
      setBackups([]);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      await chargerSauvegardes();

      // Charger les stats de cache
      try {
        const cacheData = await apiJSON('cache/status');
        if (cacheData.success) {
          setCacheStats(cacheData.stats);
        }
      } catch (error) {
        console.warn('Stats cache non disponibles:', error.message);
        setCacheStats({
          total_keys: 0,
          total_size_mb: 0,
          hit_rate: 0
        });
      }

      // Le chargement des dossiers surveillés a été retiré : le router backend
      // `automated_ingestion` est archivé (son service a été supprimé), donc
      // l'appel renvoyait 404 — toutes les 30 secondes, en boucle, pour
      // alimenter un onglet lui-même retiré de l'interface.

    } catch (error) {
      handleApiError(error, 'Chargement données gestion');
    } finally {
      setLoading(false);
    }
  };
  loadDataRef.current = loadData;

  const createBackup = async () => {
    try {
      setActionLoading(true);
      const response = await apiFetch('backup/create', {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        notificationService.showToast('Backup en cours de création...', 'success');
        setTimeout(loadData, 2000);
      } else {
        notificationService.showToast('Erreur création backup', 'error');
      }
    } catch (error) {
      handleApiError(error, 'Création backup');
    } finally {
      setActionLoading(false);
    }
  };

  const restoreBackup = async (backupName) => {
    const ok = await confirm({
      title: 'Restaurer ce backup ?',
      message: `Le backup « ${backupName} » va remplacer les données actuelles. Cette action est irréversible.`,
      confirmLabel: 'Restaurer',
      danger: true,
    });
    if (!ok) return;

    try {
      setActionLoading(true);
      const response = await apiFetch(`backup/restore/${encodeURIComponent(backupName)}`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        notificationService.showToast('Backup restauré avec succès', 'success');
        setTimeout(() => window.location.reload(), 2000);
      } else {
        notificationService.showToast('Erreur restauration', 'error');
      }
    } catch (error) {
      handleApiError(error, 'Restauration backup');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteBackup = async (backupName) => {
    const ok = await confirm({
      title: 'Supprimer ce backup ?',
      message: `Le backup « ${backupName} » sera définitivement supprimé.`,
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;

    try {
      setActionLoading(true);
      const response = await apiFetch(`backup/delete/${encodeURIComponent(backupName)}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        notificationService.showToast('Backup supprimé', 'success');
        loadData();
      } else {
        notificationService.showToast('Erreur suppression', 'error');
      }
    } catch (error) {
      handleApiError(error, 'Suppression backup');
    } finally {
      setActionLoading(false);
    }
  };

  const clearCache = async () => {
    const ok = await confirm({
      title: 'Vider le cache ?',
      message: "L'application peut être temporairement plus lente le temps que le cache se reconstitue.",
      confirmLabel: 'Vider',
    });
    if (!ok) return;

    try {
      setActionLoading(true);
      const response = await apiFetch('cache/clear', {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        notificationService.showToast(`Cache vidé: ${data.deleted_count} clés supprimées`, 'success');
        loadData();
      } else {
        notificationService.showToast('Erreur vidage cache', 'error');
      }
    } catch (error) {
      handleApiError(error, 'Vidage cache');
    } finally {
      setActionLoading(false);
    }
  };

  // Le CRUD des dossiers surveillés (addFolder / updateFolder / deleteFolder)
  // a été retiré : aucun élément d'interface ne l'appelait, et ses routes
  // `automated-ingestion/folders` n'existent plus côté serveur.

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp.replace(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/, '$1-$2-$3T$4:$5:$6')).toLocaleString('fr-FR');
  };

  if (loading) {
    return (
      <div className="data-management app-page">
        <div className="loading">
          <RefreshCw className="spinner" />
          <p>Chargement de la gestion des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="data-management app-page">
      <div className="management-header">
        <h1><Database /> Gestion de Données</h1>
        <p>Backups, cache, ingestion automatique et import/export</p>
      </div>

      {/* Onglets de navigation */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'backups' ? 'active' : ''}`}
          onClick={() => setActiveTab('backups')}
        >
          <Database /> Sauvegardes
        </button>
        <button
          className={`tab ${activeTab === 'cache' ? 'active' : ''}`}
          onClick={() => setActiveTab('cache')}
        >
          <Activity /> Cache
        </button>
        {/* Correctif audit P1-9 : onglet « Ingestion Auto » retiré — le backend
            automated-ingestion est archivé (service supprimé), tous ses appels
            renvoyaient 404. */}
        <button
          className={`tab ${activeTab === 'collections' ? 'active' : ''}`}
          onClick={() => setActiveTab('collections')}
        >
          <Book /> Collections intelligentes
        </button>
        {/* Onglet « Volumes » retiré : ses chemins, tailles et statuts « Monté »
            étaient des littéraux JSX, sans aucun appel réseau. Il présentait un
            état système entièrement inventé. */}
      </div>

      {/* Contenu des onglets */}
      <div className="tab-content">
        {/* Onglet Sauvegardes */}
        {activeTab === 'backups' && sauvegardesLocalesCoupees && (
          <div className="backups-section">
            <div className="section-header">
              <h2><Database /> Sauvegardes</h2>
            </div>
            <div className="sauvegardes-cloud" role="status" data-testid="sauvegardes-cloud">
              <Cloud aria-hidden />
              <div>
                <p className="sauvegardes-cloud__titre">
                  Les sauvegardes sont assurées par Supabase ; les sauvegardes locales sont
                  désactivées sur ce serveur.
                </p>
                <p className="sauvegardes-cloud__detail">
                  Vos livres, métadonnées et fichiers originaux sont stockés dans Supabase
                  (base de données et stockage), qui en garde des copies. Il n'y a donc
                  ni sauvegarde à créer, ni archive à restaurer depuis cette page.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'backups' && !sauvegardesLocalesCoupees && (
          <div className="backups-section">
            <div className="section-header">
              <h2><Database /> Sauvegardes</h2>
              <button
                onClick={createBackup}
                disabled={actionLoading}
                className="btn-primary"
              >
                <Download /> Nouveau Backup
              </button>
            </div>

            {backupStats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon"><Database /></div>
                  <div className="stat-content">
                    <h3>{backupStats.total_backups}</h3>
                    <p>Backups totaux</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><HardDrive /></div>
                  <div className="stat-content">
                    <h3>{formatBytes(backupStats.total_size_mb * 1024 * 1024)}</h3>
                    <p>Espace utilisé</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><Clock /></div>
                  <div className="stat-content">
                    <h3>{backupStats.auto_backups || 0}</h3>
                    <p>Backups automatiques</p>
                  </div>
                </div>
              </div>
            )}

            <div className="backups-list">
              <h3>Backups disponibles</h3>
              {backups.length === 0 ? (
                <p className="no-data">Aucun backup disponible</p>
              ) : (
                <div className="backups-grid">
                  {backups.map((backup, index) => (
                    <div key={index} className="backup-card">
                      <div className="backup-header">
                        <h4>{backup.name}</h4>
                        <span className={`backup-type ${backup.type}`}>
                          {backup.type === 'auto' ? 'Automatique' : 'Manuel'}
                        </span>
                      </div>
                      <div className="backup-details">
                        <p><Clock /> {formatDate(backup.timestamp)}</p>
                        <p><HardDrive /> {backup.size_mb} MB</p>
                      </div>
                      <div className="backup-actions">
                        <button
                          type="button"
                          onClick={() => restoreBackup(backup.name)}
                          className="btn-secondary"
                          title="Restaurer"
                          aria-label={`Restaurer ${backup.name}`}
                        >
                          <Upload />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteBackup(backup.name)}
                          className="btn-danger"
                          title="Supprimer"
                          aria-label={`Supprimer ${backup.name}`}
                        >
                          <Trash2 />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onglet Cache */}
        {activeTab === 'cache' && (
          <div className="cache-section">
            <div className="section-header">
              <h2><Activity /> Cache Système</h2>
              <button
                onClick={clearCache}
                disabled={actionLoading}
                className="btn-warning"
              >
                <Trash2 /> Vider Cache
              </button>
            </div>

            {cacheStats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon"><Activity /></div>
                  <div className="stat-content">
                    <h3>{cacheStats.backend || 'Inconnu'}</h3>
                    <p>Type de cache</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><Database /></div>
                  <div className="stat-content">
                    <h3>{cacheStats.total_keys || 0}</h3>
                    <p>Clés en cache</p>
                  </div>
                </div>
                {cacheStats.backend === 'redis' && (
                  <>
                    <div className="stat-card">
                      <div className="stat-icon"><HardDrive /></div>
                      <div className="stat-content">
                        <h3>{cacheStats.used_memory_human || 'N/A'}</h3>
                        <p>Mémoire utilisée</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon"><CheckCircle /></div>
                      <div className="stat-content">
                        <h3>{cacheStats.keyspace_hits || 0}</h3>
                        <p>Hits cache</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Correctif audit P1-9 : bloc « Ingestion Auto » retiré (backend
            automated-ingestion archivé -> 404). Restauration = feature à part. */}

        {/* Onglet Collections intelligentes */}
        {activeTab === 'collections' && (
          <div className="collections-section">
            <SmartCollections />
          </div>
        )}

        {/* Onglet Volumes */}
      </div>
    </div>
  );
};

export default DataManagement;
